import React from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { LoadingScreen } from '@/components/LoadingScreen'
import {
  useHoy,
  type AgendaAction,
  type CompromisoTrabajo,
  type FocoTipo,
  type Meeting,
  type TareaToDo,
} from '@/hooks/useHoy'
import { synchronizationMessage, useCommitments } from '@/hooks/useCommitments'
import { useRelationships } from '@/hooks/useRelationships'
import { usePersonas } from '@/hooks/usePersonas'
import { CommercialSignalsPanel } from '@/components/commercial/CommercialSignalsPanel'
import { useCommercialSignals } from '@/hooks/useCommercialSignals'
import './hoy.css'

const SECTORS = ['Retail', 'Industria', 'Logistica'] as const

function sectorBucket(segmento: string | null): 'Retail' | 'Industria' | 'Logistica' | 'Otros' {
  const match = SECTORS.find((s) => s.toLowerCase() === (segmento ?? '').trim().toLowerCase())
  return match ?? 'Otros'
}

/**
 * Resumen comercial (MASTER.md AA-14): cada número enlaza a una vista
 * filtrada de Empresas, nunca es un contador suelto. Se calcula client-side
 * desde las mismas listas que ya usan /relaciones y /personas — sin tocar el
 * contrato de /api/hoy.
 */
function useResumenComercial() {
  const { status: relStatus, relationships } = useRelationships()
  const { status: perStatus, personas } = usePersonas()

  const resumen = React.useMemo(() => {
    if (relStatus !== 'ready' || perStatus !== 'ready') return null

    const orgConPersona = new Set(personas.map((p) => p.organizacionId).filter(Boolean))
    const empresasActivas = relationships
    const sectores: Record<'Retail' | 'Industria' | 'Logistica' | 'Otros', number> = {
      Retail: 0,
      Industria: 0,
      Logistica: 0,
      Otros: 0,
    }
    let sinPersona = 0
    let sinAccion = 0
    let conCompromiso = 0
    let sinContexto = 0

    for (const org of empresasActivas) {
      sectores[sectorBucket(org.segmento)] += 1
      if (!orgConPersona.has(org.id)) sinPersona += 1
      if (!org.nextAction) sinAccion += 1
      else conCompromiso += 1
      if (org.signal === 'sin_contexto_suficiente') sinContexto += 1
    }

    const personasSinCorreo = personas.filter((p) => !p.email).length

    return {
      totalEmpresas: empresasActivas.length,
      totalPersonas: personas.length,
      sectoresActivos: Object.entries(sectores).filter(([, n]) => n > 0).length,
      sinPersona,
      sinAccion,
      conCompromiso,
      sinContexto,
      sectores,
      personasSinCorreo,
    }
  }, [relStatus, relationships, perStatus, personas])

  return {
    resumen,
    loading: relStatus === 'loading' || perStatus === 'loading',
  }
}

type ResumenComercialData = NonNullable<ReturnType<typeof useResumenComercial>['resumen']>

const ResumenComercial: React.FC<{ resumen: ResumenComercialData }> = ({ resumen }) => {
  return (
    <section className="hoy__resumen" aria-label="Cobertura comercial">
      <div className="hoy__resumenCol">
        <div className="hoy__sectionTitle">Cobertura comercial</div>
        <p className="hoy__sectionLead">Referencia secundaria para revisar calidad y alcance de los datos.</p>
        <div className="hoy__statRow">
          <Link to="/relaciones" className="hoy__stat">
            <strong>{resumen.totalEmpresas}</strong>
            <span>Empresas</span>
          </Link>
          <Link to="/personas" className="hoy__stat">
            <strong>{resumen.totalPersonas}</strong>
            <span>Personas identificadas</span>
          </Link>
          <span className="hoy__stat hoy__stat--static">
            <strong>{resumen.sectoresActivos}</strong>
            <span>Sectores activos</span>
          </span>
          <Link to="/relaciones?filtro=sinPersona" className="hoy__stat">
            <strong>{resumen.sinPersona}</strong>
            <span>Sin persona</span>
          </Link>
          <Link to="/relaciones?filtro=sinAccion" className="hoy__stat">
            <strong>{resumen.sinAccion}</strong>
            <span>Sin próxima acción</span>
          </Link>
          <Link to="/relaciones?filtro=conCompromiso" className="hoy__stat">
            <strong>{resumen.conCompromiso}</strong>
            <span>Con compromiso abierto</span>
          </Link>
          <Link to="/relaciones?filtro=contexto" className="hoy__stat">
            <strong>{resumen.sinContexto}</strong>
            <span>Sin contexto suficiente</span>
          </Link>
        </div>

        <div className="hoy__sectorRow">
          <Link to="/relaciones?filtro=retail" className="hoy__sectorChip">
            Retail: {resumen.sectores.Retail}
          </Link>
          <Link to="/relaciones?filtro=industria" className="hoy__sectorChip">
            Industria: {resumen.sectores.Industria}
          </Link>
          <Link to="/relaciones?filtro=logistica" className="hoy__sectorChip">
            Logistica: {resumen.sectores.Logistica}
          </Link>
          {resumen.sectores.Otros > 0 && (
            <Link to="/relaciones" className="hoy__sectorChip">
              Otros: {resumen.sectores.Otros}
            </Link>
          )}
        </div>
      </div>
    </section>
  )
}

const focoEyebrow: Record<FocoTipo, string> = {
  compromiso_vencido: 'Compromiso vencido',
  compromiso: 'Compromiso próximo',
  reunion: 'Reunión próxima',
  relacion: 'Relación',
}

const AgendaPrioritaria: React.FC<{
  actions: AgendaAction[]
  total: number
  missingSources: string[]
  onComplete: (id: string) => Promise<string>
}> = ({ actions, total, missingSources, onComplete }) => {
  const [workingId, setWorkingId] = React.useState<string | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)
  const topActions = actions.slice(0, 5)
  const scoreCounts = React.useMemo(() => {
    const counts = new Map<number, number>()
    topActions.forEach((action) => counts.set(action.score, (counts.get(action.score) ?? 0) + 1))
    return counts
  }, [topActions])
  const hasTies = [...scoreCounts.values()].some((count) => count > 1)

  const complete = async (action: AgendaAction) => {
    if (!action.command.id) return
    setWorkingId(action.id)
    setNotice(null)
    try {
      setNotice(await onComplete(action.command.id))
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'No se pudo completar el compromiso.')
    } finally {
      setWorkingId(null)
    }
  }

  return (
    <section className="hoy__agenda" aria-labelledby="agenda-prioritaria-title">
      <div className="hoy__agendaHeader">
        <div>
          <div className="hoy__agendaEyebrow">Centro de mando comercial</div>
          <h2 id="agenda-prioritaria-title">Tus 5 acciones prioritarias</h2>
          <p>
            Urgencia, prioridad y salud comercial en un orden verificable. Los puntos explican la posición.
          </p>
          {hasTies && (
            <p className="hoy__tieExplanation">
              Hay prioridades empatadas. agenda-v2 las ordena por fecha, empresa e identificador estable.
            </p>
          )}
        </div>
        <span className="hoy__agendaCount">
          {Math.min(actions.length, 5)} de {total}
        </span>
      </div>
      {missingSources.length > 0 && (
        <p className="hoy__agendaWarning" role="status">
          Fuentes no disponibles: {missingSources.join(', ')}. La agenda no interpreta su ausencia como “al día”.
        </p>
      )}
      {notice && (
        <p className="hoy__agendaNotice" role="status">
          {notice}
        </p>
      )}
      {actions.length === 0 ? (
        <p className="hoy__calendarState">No hay acciones verificables con la evidencia disponible.</p>
      ) : (
        <ol className="hoy__agendaList">
          {topActions.map((action, index) => {
            const reasonParts = action.razon.split(' · ').filter(Boolean)
            const isTied = (scoreCounts.get(action.score) ?? 0) > 1
            const primaryHref = action.command.kind === 'context' && action.tipo === 'relacion'
              ? `${action.href}#nueva-accion`
              : action.href
            const primaryLabel = action.tipo === 'relacion'
              ? 'Registrar próxima acción'
              : action.tipo === 'reunion'
                ? 'Preparar reunión'
                : 'Completar compromiso'
            return (
            <li key={action.id} className={`hoy__agendaItem${isTied ? ' hoy__agendaItem--tied' : ''}`}>
              <div className="hoy__agendaRank" aria-label={`Prioridad ${index + 1}`}>
                {index + 1}
              </div>
              <div className="hoy__agendaMain">
                <div className="hoy__agendaMeta">
                  <span>{focoEyebrow[action.tipo]}</span>
                  {action.prioridad && <span>{action.prioridad}</span>}
                  <span>{action.score} puntos</span>
                  {isTied && <span className="hoy__tieBadge">Empate</span>}
                </div>
                <h3>{action.titulo}</h3>
                <p className="hoy__agendaWho">
                  {action.organizacionNombre}
                  {action.personaNombre ? ` · ${action.personaNombre}` : ''}
                </p>
                <p className="hoy__agendaReason"><strong>Por qué ahora:</strong> {reasonParts[0]}</p>
                {reasonParts.length > 1 && (
                  <details className="hoy__agendaReasonMore">
                    <summary>+{reasonParts.length - 1} señal{reasonParts.length === 2 ? '' : 'es'} adicional{reasonParts.length === 2 ? '' : 'es'}</summary>
                    <ul>{reasonParts.slice(1).map((reason) => <li key={reason}>{reason}</li>)}</ul>
                  </details>
                )}
                <p className="hoy__agendaNext">
                  <strong>Siguiente acción:</strong> {action.accionSugerida}
                </p>
                <details className="hoy__agendaEvidence">
                  <summary>Evidencia y puntuación</summary>
                  <ul>
                    {action.factors
                      .filter((factor) => factor.points > 0)
                      .map((factor) => (
                        <li key={factor.code}>
                          {factor.label}: +{factor.points}
                        </li>
                      ))}
                    {action.evidence.map((item) => (
                      <li key={`${item.source}:${item.id}`}>
                        {item.source}: {item.label}
                        {item.date ? ` · ${formatDate(item.date)}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
              <div className="hoy__agendaActions">
                {action.command.kind === 'context' ? (
                  <Link to={primaryHref} className="hoy__agendaPrimaryAction">{primaryLabel}</Link>
                ) : action.command.id ? (
                  <button type="button" disabled={workingId === action.id} onClick={() => complete(action)}>
                    {workingId === action.id ? 'Completando…' : 'Completar'}
                  </button>
                ) : null}
                <Link to={action.href} className="hoy__agendaContextLink">Ver contexto</Link>
              </div>
            </li>
          )})}
        </ol>
      )}
    </section>
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es', { day: 'numeric', month: 'long' })
}

function formatCommitmentDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-')
  return year && month && day ? `${day}/${month}/${year}` : iso
}

function formatMeetingRange(meeting: Meeting): string {
  const start = new Date(meeting.inicio)
  if (Number.isNaN(start.getTime())) return meeting.inicio

  const day = start.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })
  if (meeting.todoElDia) return `${day} · Todo el día`

  const startTime = start.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  const end = meeting.fin ? new Date(meeting.fin) : null
  const endTime =
    end && !Number.isNaN(end.getTime()) ? end.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : null
  return `${day} · ${startTime}${endTime ? `–${endTime}` : ''}`
}

const ProximasReuniones: React.FC<{
  status: 'ok' | 'error' | 'not_configured'
  reuniones: Meeting[]
}> = ({ status, reuniones }) => (
  <section className="hoy__section hoy__calendar" aria-labelledby="proximas-reuniones-title">
    <div className="hoy__calendarHeader">
      <div id="proximas-reuniones-title" className="hoy__sectionTitle">
        Próximas reuniones
      </div>
      <span className="hoy__calendarWindow">7 días · Solo lectura</span>
    </div>

    {status !== 'ok' ? (
      <p className="hoy__calendarState">
        Calendar no está disponible ahora. El resto de Hoy continúa funcionando con los datos de Sales.
      </p>
    ) : reuniones.length === 0 ? (
      <p className="hoy__calendarState">Sin reuniones próximas en los próximos 7 días.</p>
    ) : (
      <div className="hoy__meetingList">
        {reuniones.map((meeting) => {
          const contexto = meeting.contextoSugerido
          const contextHref = contexto?.personaId
            ? `/personas/${contexto.personaId}`
            : contexto?.organizacionId
              ? `/relaciones/${contexto.organizacionId}`
              : null
          const contextLabel = [contexto?.personaNombre, contexto?.organizacionNombre].filter(Boolean).join(' · ')
          const participantParts = [
            meeting.organizador ? `Organiza ${meeting.organizador}` : null,
            meeting.asistentes.length > 0 ? `Con ${meeting.asistentes.join(', ')}` : null,
          ].filter(Boolean)

          return (
            <article key={meeting.id} className="hoy__meeting">
              <time className="hoy__meetingTime" dateTime={meeting.inicio}>
                {formatMeetingRange(meeting)}
              </time>
              <div className="hoy__meetingMain">
                <div className="hoy__meetingTitle">{meeting.asunto}</div>
                {participantParts.length > 0 && (
                  <div className="hoy__meetingPeople">{participantParts.join(' · ')}</div>
                )}
                <div className={`hoy__meetingContext ${contexto ? 'hoy__meetingContext--matched' : ''}`}>
                  {contexto ? `Contexto sugerido: ${contextLabel}` : 'Sin relación detectada'}
                </div>
              </div>
              {contextHref && (
                <Link to={contextHref} className="hoy__link hoy__meetingAction">
                  Ver contexto
                </Link>
              )}
            </article>
          )
        })}
      </div>
    )}
  </section>
)

const commitmentLabel: Record<CompromisoTrabajo['momento'], string> = {
  vencido: 'Vencido',
  hoy: 'Hoy',
  proximo: 'Próximo',
}

const CompromisosProximos: React.FC<{
  status: 'ok' | 'error'
  compromisos: CompromisoTrabajo[]
}> = ({ status, compromisos }) => (
  <section className="hoy__section hoy__commitments" aria-labelledby="compromisos-proximos-title">
    <div className="hoy__calendarHeader">
      <div id="compromisos-proximos-title" className="hoy__sectionTitle">
        Compromisos próximos
      </div>
      <span className="hoy__calendarWindow">Vencidos, hoy y 7 días · Solo lectura</span>
    </div>

    {status !== 'ok' ? (
      <p className="hoy__calendarState">No pudimos leer los compromisos ahora. El resto de Hoy continúa disponible.</p>
    ) : compromisos.length === 0 ? (
      <p className="hoy__calendarState">No hay compromisos vencidos ni próximos para los siguientes 7 días.</p>
    ) : (
      <div className="hoy__commitmentList">
        {compromisos.map((commitment) => {
          const contextHref = commitment.personaId
            ? `/personas/${commitment.personaId}`
            : commitment.organizacionId
              ? `/relaciones/${commitment.organizacionId}`
              : null

          return (
            <article key={commitment.id} className={`hoy__commitment hoy__commitment--${commitment.momento}`}>
              <div className="hoy__commitmentWhen">
                <span className={`hoy__commitmentLabel hoy__commitmentLabel--${commitment.momento}`}>
                  {commitmentLabel[commitment.momento]}
                </span>
                <time dateTime={commitment.vencimiento}>{formatCommitmentDate(commitment.vencimiento)}</time>
              </div>
              <div className="hoy__commitmentMain">
                <div className="hoy__commitmentTitle">{commitment.title}</div>
                <div className="hoy__commitmentContext">
                  {commitment.organizacionNombre ?? 'Empresa no disponible'}
                  {commitment.personaNombre ? ` · ${commitment.personaNombre}` : ''}
                  {` · Estado: ${commitment.estado}`}
                </div>
                {commitment.descripcion && <p className="hoy__commitmentDescription">{commitment.descripcion}</p>}
              </div>
              {contextHref && (
                <Link to={contextHref} className="hoy__link hoy__meetingAction">
                  Ver contexto
                </Link>
              )}
            </article>
          )
        })}
      </div>
    )}
  </section>
)

const todoLabel: Record<TareaToDo['momento'], string> = {
  vencido: 'Vencida',
  hoy: 'Hoy',
  proximo: 'Próxima',
}

const todoStatusLabel: Record<string, string> = {
  notStarted: 'No iniciada',
  inProgress: 'En progreso',
  waitingOnOthers: 'En espera',
  deferred: 'Aplazada',
}

const TareasMicrosoftToDo: React.FC<{
  status: 'ok' | 'error' | 'not_configured'
  tareas: TareaToDo[]
}> = ({ status, tareas }) => (
  <section className="hoy__section hoy__todo" aria-labelledby="tareas-microsoft-todo-title">
    <div className="hoy__calendarHeader">
      <div id="tareas-microsoft-todo-title" className="hoy__sectionTitle">
        Tareas Microsoft To Do
      </div>
      <span className="hoy__calendarWindow">Vencidas, hoy y 7 días · Solo lectura</span>
    </div>

    {status !== 'ok' ? (
      <p className="hoy__calendarState">
        Microsoft To Do no está disponible ahora. Tus compromisos de Sales continúan sin cambios.
      </p>
    ) : tareas.length === 0 ? (
      <p className="hoy__calendarState">No hay tareas abiertas vencidas ni próximas para los siguientes 7 días.</p>
    ) : (
      <div className="hoy__commitmentList">
        {tareas.map((task) => (
          <article
            key={`${task.listId}:${task.id}`}
            className={`hoy__commitment hoy__todoTask hoy__commitment--${task.momento}`}
          >
            <div className="hoy__commitmentWhen">
              <span className={`hoy__commitmentLabel hoy__commitmentLabel--${task.momento}`}>
                {todoLabel[task.momento]}
              </span>
              <time dateTime={task.dueDate}>{formatCommitmentDate(task.dueDate)}</time>
            </div>
            <div className="hoy__commitmentMain">
              <div className="hoy__commitmentTitle">{task.title}</div>
              {task.divergence && (
                <p role="status">
                  Hay diferencias pendientes de sincronización.{' '}
                  <Link to={`/relaciones/${task.organizacionId}`}>Comprobar compromiso</Link>
                </p>
              )}
              <div className="hoy__commitmentContext">
                {task.listName} · {task.estadoSales ? `Sales: ${task.estadoSales} · Microsoft: ` : 'Estado: '}
                {todoStatusLabel[task.status] ?? task.status}
              </div>
            </div>
          </article>
        ))}
      </div>
    )}
  </section>
)

export const Hoy: React.FC = () => {
  const { status, data, error, reload } = useHoy()
  const resumenComercial = useResumenComercial()
  const { updateCommitment } = useCommitments()
  const signals = useCommercialSignals()

  if (status === 'loading' || resumenComercial.loading) {
    return <LoadingScreen overlay />
  }
  if (status === 'not_signed_in') {
    return (
      <div className="hoy">
        <PageHeader title="Hoy" />
        <p className="hoy__state">Inicia sesión con tu cuenta Microsoft para ver tu escritorio comercial.</p>
      </div>
    )
  }
  if (status === 'not_configured') {
    return (
      <div className="hoy">
        <PageHeader title="Hoy" />
        <p className="hoy__state">La conexión con SharePoint aún no está habilitada en este entorno.</p>
      </div>
    )
  }
  if (status === 'forbidden') {
    return (
      <div className="hoy">
        <PageHeader title="Hoy" />
        <p className="hoy__state">Tu perfil aún no está habilitado en Wacner Sales.</p>
      </div>
    )
  }
  if (status === 'unauthorized' || status === 'error' || !data) {
    return (
      <div className="hoy">
        <PageHeader title="Hoy" />
        <p className="hoy__state">
          No se pudo cargar Hoy{error instanceof Error ? `: ${error.message}` : '.'}{' '}
          <button
            className="hoy__link"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            onClick={reload}
          >
            Reintentar
          </button>
        </p>
      </div>
    )
  }

  const {
    agenda,
    compromisosTrabajo = [],
    graph,
    sharePoint = { compromisos: 'error' },
    reuniones = [],
    tareasToDo = [],
  } = data

  const completeFromAgenda = async (id: string) => {
    const result = await updateCommitment(id, { estado: 'completado' })
    reload()
    return `Compromiso completado. ${synchronizationMessage(result.synchronization)}`
  }

  return (
    <div className="hoy">
      <PageHeader title="Hoy" subtitle={`${formatDate(data.fecha)} · ${data.identidad.nombre}`} />

      <AgendaPrioritaria
        actions={agenda.actions}
        total={agenda.total}
        missingSources={agenda.missingSources}
        onComplete={completeFromAgenda}
      />

      <CommercialSignalsPanel
        signals={signals.status === 'ready' ? signals.signals : (data.commercialSignals?.items ?? [])}
        loading={signals.status === 'loading'}
        onRefresh={() => signals.refresh()}
        onTransition={signals.transition}
      />

      {resumenComercial.resumen && <ResumenComercial resumen={resumenComercial.resumen} />}

      <CompromisosProximos status={sharePoint.compromisos} compromisos={compromisosTrabajo} />

      <ProximasReuniones status={graph.calendario} reuniones={reuniones} />

      <TareasMicrosoftToDo status={graph.todo} tareas={tareasToDo} />
    </div>
  )
}
